import { useEffect, type ReactNode } from 'react';

import { setRegistrationTargetRole, type RegistrationRole } from '../features/registration/model';
import { useAuth, type UserRole } from '../state/auth';

export type MissingGuardVisual = 'BLOCKED_BY_FIGMA';

interface AuthReturnIntent {
  hash: string;
  role: RegistrationRole;
}

const AUTH_RETURN_KEY = 'lovo:v2:auth-return';

function guardedRole(allow?: readonly UserRole[]): RegistrationRole | null {
  if (allow?.length !== 1) return null;
  return allow[0] === 'customer' || allow[0] === 'carrier' ? allow[0] : null;
}

function rememberAuthReturn(allow?: readonly UserRole[]) {
  if (sessionStorage.getItem(AUTH_RETURN_KEY)) return;
  const role = guardedRole(allow);
  if (!role) return;
  const hash = window.location.hash;
  if (!/^#[a-z0-9-]+(?:\?.*)?$/.test(hash)) return;
  const intent: AuthReturnIntent = { hash, role };
  sessionStorage.setItem(AUTH_RETURN_KEY, JSON.stringify(intent));
  setRegistrationTargetRole(role);
}

export function consumeAuthReturn(role: RegistrationRole, fallback: string): string {
  try {
    const raw = sessionStorage.getItem(AUTH_RETURN_KEY);
    sessionStorage.removeItem(AUTH_RETURN_KEY);
    if (!raw) return fallback;
    const intent = JSON.parse(raw) as Partial<AuthReturnIntent>;
    if (intent.role !== role || typeof intent.hash !== 'string') return fallback;
    return /^#(?!login$)[a-z0-9-]+(?:\?.*)?$/.test(intent.hash) ? intent.hash : fallback;
  } catch {
    sessionStorage.removeItem(AUTH_RETURN_KEY);
    return fallback;
  }
}

function roleHome(role: UserRole): string {
  if (role === 'carrier') return '#carrier-dashboard';
  if (role === 'customer') return '#my-requests';
  return '#landing';
}

export interface AuthGuardProps {
  allow?: readonly UserRole[];
  children: ReactNode;
  denied?: ReactNode;
  error?: ReactNode;
  loading?: ReactNode;
  onDenied?: (reason: 'guest' | 'role') => void;
}

export function AuthGuard({ allow, children, denied = null, error = null, loading = null, onDenied }: AuthGuardProps) {
  const session = useAuth();
  const deniedReason = session.status === 'guest'
    ? 'guest'
    : session.status === 'authenticated' && allow && !allow.includes(session.user!.active_role)
      ? 'role'
      : null;

  useEffect(() => {
    if (!deniedReason) return;
    onDenied?.(deniedReason);
    if (deniedReason === 'guest') {
      rememberAuthReturn(allow);
      window.location.replace('#login');
      return;
    }
    window.location.replace(roleHome(session.user!.active_role));
  }, [allow, deniedReason, onDenied, session.user]);

  if (session.status === 'loading') return loading;
  if (session.status === 'error') return error;
  if (deniedReason) return denied;
  if (session.status !== 'authenticated') return denied;
  return children;
}

export const missingGuardVisual: MissingGuardVisual = 'BLOCKED_BY_FIGMA';
